import { useEffect, useRef, useState } from 'react';
import { ArrowRight, BookOpen, HelpCircle, MessageSquarePlus, MessagesSquare } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { SUPPORT_TYPES, SupportType } from '../../../services/support/supportService';
import { useSupportIdentity, useSupportStore } from '../../../stores/appSupportStore';
import { borderGrey, cardBg, greenButtonClass, greenTextColor, lightGreenBG, primaryText, secondaryText } from '../../../styling/stylingHelper';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import { SUPPORT_DRAWER_TRANSITION_MS, SupportDrawer } from './SupportDrawer';
import { useSupportDraftGuard } from './useSupportDraftGuard';

export default function SupportPage() {
  const identity = useSupportIdentity();
  return identity ? <SupportHome key={identity} /> : null;
}

function SupportHome() {
  const { t } = useTranslation('support');
  const [params, setParams] = useSearchParams();
  const [shown, setShown] = useState(params.toString());
  const dirty = useRef(false);
  useSupportDraftGuard(dirty, t('discardDraft'));
  const unread = useSupportStore(state => state.unreadCount);
  const shownParams = new URLSearchParams(shown);
  const threadId = shownParams.get('thread');
  const view = threadId || shownParams.get('view') === 'submitted' ? 'submitted' : shownParams.get('view') === 'new' ? 'new' : null;
  const typeValue = shownParams.get('type');
  const type: SupportType = SUPPORT_TYPES.find(value => value === typeValue) ?? 'support';

  const canLeave = () => !dirty.current || window.confirm(t('discardDraft'));
  useEffect(() => {
    const next = params.toString();
    if (next === shown) return;
    if (dirty.current && !window.confirm(t('discardDraft'))) { setParams(new URLSearchParams(shown), { replace: true }); return; }
    dirty.current = false; setShown(next);
  }, [params, shown, setParams, t]);
  const navigateDrawer = (nextView: 'new' | 'submitted', id?: string, nextType?: SupportType) => {
    // Selecting the current tab/thread is a no-op, not permission to forget its draft.
    if (nextView === view && (nextView === 'new' ? !nextType || nextType === type : (id ?? null) === threadId)) return;
    if (!canLeave()) return;
    dirty.current = false;
    const next = new URLSearchParams(params);
    next.delete('thread'); next.delete('type'); next.set('view', nextView);
    if (id) next.set('thread', id);
    if (nextType) next.set('type', nextType);
    setShown(next.toString()); setParams(next);
  };
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef<number>();
  const latestParams = useRef(params); latestParams.current = params;
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);
  const close = () => {
    if (closing || !canLeave()) return;
    dirty.current = false;
    setClosing(true);
    // Keep the drawer mounted while it slides out, then drop its query state.
    closeTimer.current = window.setTimeout(() => {
      const next = new URLSearchParams(latestParams.current); next.delete('thread'); next.delete('view'); next.delete('type');
      setClosing(false); setShown(next.toString()); setParams(next, { replace: true });
    }, SUPPORT_DRAWER_TRANSITION_MS);
  };
  const card = cn('rounded-3xl border p-5 shadow-sm sm:p-7', borderGrey, cardBg);
  return <>
    <MobilePageHeader title={t('navLabel')} />
    <main className={cn('mx-auto max-w-7xl space-y-6 p-4 pb-28 sm:p-6 lg:p-8', primaryText)}>
      <header><h1 className="hidden text-3xl font-bold lg:block">{t('title')}</h1><p className={cn('mt-2 text-base sm:text-lg', secondaryText)}>{t('subtitle')}</p></header>
      <section className={cn(card, 'space-y-5 sm:p-9')}>
        <div className={cn('inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium', lightGreenBG, greenTextColor)}><MessageSquarePlus size={18} aria-hidden="true" />{t('heroBadge')}</div>
        <h2 className="text-2xl font-bold sm:text-3xl">{t('heroTitle')}</h2>
        <p className={cn('max-w-3xl text-base sm:text-lg', secondaryText)}>{t('heroDescription')}</p>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">{SUPPORT_TYPES.map(value => <button key={value} type="button" onClick={() => navigateDrawer('new', undefined, value)}
          className={value === 'support' ? cn(greenButtonClass, 'min-h-[48px] justify-center') : cn('flex min-h-[48px] items-center justify-center rounded-xl border px-5 py-3', borderGrey, primaryText)}>{t(`actions.${value}`)}</button>)}</div>
      </section>
      <section className="grid grid-cols-1 gap-5 lg:grid-cols-3" aria-label={t('resources')}>
        <Link to="/support/guides" className={cn(card, 'flex flex-col items-start gap-4')}><BookOpen className={cn('h-12 w-12 rounded-2xl p-3', lightGreenBG, greenTextColor)} /><h2 className="text-xl font-semibold">{t('guidesTitle')}</h2><p className={cn('flex-1', secondaryText)}>{t('guidesDescription')}</p><span className={cn('flex items-center gap-2 font-medium', greenTextColor)}>{t('guidesAction')}<ArrowRight size={18} /></span></Link>
        <Link to="/support/faq" className={cn(card, 'flex flex-col items-start gap-4')}><HelpCircle className={cn('h-12 w-12 rounded-2xl p-3', lightGreenBG, greenTextColor)} /><h2 className="text-xl font-semibold">{t('faqTitle')}</h2><p className={cn('flex-1', secondaryText)}>{t('faqDescription')}</p><span className={cn('flex items-center gap-2 font-medium', greenTextColor)}>{t('faqAction')}<ArrowRight size={18} /></span></Link>
        <button type="button" onClick={() => navigateDrawer('submitted')} className={cn(card, 'flex flex-col items-start gap-4 text-left')}><MessagesSquare className={cn('h-12 w-12 rounded-2xl p-3', lightGreenBG, greenTextColor)} /><h2 className="text-xl font-semibold">{t('submitted')}</h2><p className={cn('flex-1', secondaryText)}>{t('submittedDescription')}</p>{unread > 0 && <span className={cn('rounded-full px-3 py-1 text-sm', lightGreenBG, greenTextColor)}>{t('unreadLabel', { count: unread })}</span>}<span className={cn('flex items-center gap-2 font-medium', greenTextColor)}>{t('submittedAction')}<ArrowRight size={18} /></span></button>
      </section>
    </main>
    {view && <SupportDrawer view={view} threadId={threadId} type={type} closing={closing} onNavigate={navigateDrawer} onClose={close} onDraftChange={value => { dirty.current = value; }} />}
  </>;
}
